import { CommonModule, DatePipe } from '@angular/common';
import { Component, Input, ChangeDetectionStrategy, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { SvgIcon, SchoolData, SchoolAddress as Address } from '@k12/models';
import { PhonePipe } from '@k12/pipes';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'lib-school-info',
  standalone: true,
  imports: [CommonModule, DatePipe, MatButtonModule],
  templateUrl: './school-info.component.html',
  providers: [PhonePipe]
})
export class SchoolInfoComponent {
  private readonly phonePipe = inject(PhonePipe);
  private _schoolData: SchoolData = {};

  @Input() set schoolData(value: SchoolData) {
    this._schoolData = Array.isArray(value) ? {} : value || {};
  }
  get schoolData(): SchoolData {
    return this._schoolData;
  }

  readonly editIcon: SvgIcon = SvgIcon.Edit;

  getAddressWithLineBreaks(address?: Address | string): string {
    // Handle cases where address is not provided
    if (!address) {
      return '-';
    }

    // If address is a string, process it normally
    //this adress is displayed in address section, and we dont need county
    // county is removed from address string
    if (typeof address === 'string') {
      const plainAddress = address.replaceAll('<br>', '\n');
      const lines = plainAddress.split('\n');

      const lastLine = lines.at(-1) ?? '';
      const parts = lastLine.split(',');

      // city, county, state, zip
      if (parts.length >= 4) {
        const removeCounty = [
          parts[0].trim(), // city
          parts[2].trim(), // state
          parts[3].trim() // zip
        ].join(', ');

        lines.splice(-1, 1, removeCounty);
      }

      if (lines.length > 2) {
        lines.splice(1, 1); // remove address2
      }

      return lines.join('<br>');
    }
    // If address is an object, try to construct a string from it
    const addressParts: string[] = [];

    // Add street address lines
    if (address.address1) addressParts.push(address.address1);

    // Add city, state, zip on one line
    const cityStateZip: string[] = [];
    if (address.city) cityStateZip.push(address.city);
    if (address.state) {
      // Handle state object or string
      const stateName = typeof address.state === 'object' ? address.state.name : address.state;
      if (stateName) cityStateZip.push(stateName);
    }

    if (address.zip) cityStateZip.push(address.zip);

    if (cityStateZip.length > 0) {
      addressParts.push(cityStateZip.join(', '));
    }

    const addressString = addressParts.join('<br>');
    return addressString || '-';
  }

  get county(): string {
    return this.schoolData.addresses?.[0]?.county || '-';
  }

  get phoneExtension(): string {
    return this.schoolData.phoneExtension?.trim() || '';
  }

  get formattedPhone(): string {
    const raw = this.schoolData.phone?.trim();
    if (!raw) {
      return '-';
    }

    const phone = this.phonePipe.transform(raw);
    const extension = this.phoneExtension;

    return extension ? `${phone} ext. ${extension}` : phone;
  }
}
